// V4-FRONT-HEADERS-UNIFORM 2026-07-02 — coque de header partagée pour tous les
// onglets/pages du dashboard. Référence visuelle : le header d'EngagementTab
// (p-4, icône primary, h2 sans graisse forcée, badge count, actions ghost à
// droite, borderBottom var(--color-border)). Avant ce composant, chaque tab
// dupliquait sa coque avec des paddings divergents (p-4 vs p-6 vs px-6 py-4)
// → hauteurs de header incohérentes d'une page à l'autre.
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';

interface TabHeaderProps {
  /** Icône optionnelle à gauche du titre (ex: <MessageSquare className="w-5 h-5" …/>). */
  icon?: ReactNode;
  title: ReactNode;
  /** Badge count standard (style Engagement). Rendu seulement si défini. */
  count?: number;
  /** Éléments additionnels dans le groupe titre (tooltips info, warnings…). */
  titleExtras?: ReactNode;
  /** Actions à droite (boutons spécifiques au tab), avant la croix de fermeture. */
  actions?: ReactNode;
  /** Rend la croix de fermeture standard (ghost, rounded-full) si fourni. */
  onClose?: () => void;
  /** Ligne(s) additionnelle(s) sous la ligne titre, dans la même coque. */
  children?: ReactNode;
}

export function TabHeader({ icon, title, count, titleExtras, actions, onClose, children }: TabHeaderProps) {
  return (
    <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--color-border)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h2 style={{ color: 'var(--color-text-primary)' }}>{title}</h2>
          {count !== undefined && (
            <span
              className="ml-2 text-xs px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: 'var(--color-primary-light, rgba(124, 58, 237, 0.15))',
                color: 'var(--color-primary)',
              }}
            >
              {count}
            </span>
          )}
          {titleExtras}
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
              <X className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
            </Button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
